import React, { useState, useRef, useEffect } from 'react';
import { useSocket } from '../../context/SocketContext';
import { ChatMessage } from '../../services/socketService';
import { chatService } from '../../services/chatService';
import { assemblyService } from '../../services/assemblyService';
import LoadingState from '../common/LoadingState';
import { notifyError, notifySuccess } from '../../utils/notificationCenter';

interface ChatComponentProps {
  assemblyId: string;
  className?: string;
  maxHeight?: string;
  userRole?: 'admin' | 'manager' | 'owner' | 'tenant' | 'guest';
}

const MIN_LOADING_MS = 250;

const ensureMinimumLoadingTime = async (startTime: number) => {
  const elapsed = Date.now() - startTime;
  if (elapsed < MIN_LOADING_MS) {
    await new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS - elapsed));
  }
};

const ChatComponent: React.FC<ChatComponentProps> = ({ 
  assemblyId, 
  className = '',
  maxHeight = 'h-96',
  userRole,
}) => {
  const { chatMessages, typingUsers, sendMessage, setTyping, isConnected, loadHistoricalMessages } = useSocket();
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingChat, setIsDownloadingChat] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadedAssemblyRef = useRef<string | null>(null); // 🔥 Track which assembly has been loaded
  const canDownloadChatPdf = userRole === 'admin';

  // 🔥 Cargar historial de mensajes al montar o cambiar de asamblea
  useEffect(() => {
    const loadChatHistory = async () => {
      // Evitar cargar si ya cargamos esta asamblea
      if (loadedAssemblyRef.current === assemblyId) return;
      
      const startTime = Date.now();
      setIsLoadingHistory(true);
      try {
        console.log('📥 Cargando historial de mensajes para asamblea:', assemblyId);
        const messages = await chatService.getRecentMessages(assemblyId, 100);
        
        // Transformar los mensajes del backend al formato del socket
        const formattedMessages: ChatMessage[] = messages.map(msg => ({
          id: msg.id,
          assemblyId: msg.assemblyId,
          userId: msg.userId || '',
          userName: msg.userName,
          userRole: msg.userRole || '',
          message: msg.message,
          type: (msg.type === 'system' ? 'system' : 'text') as 'text' | 'system', // 🔥 Mapear tipos correctamente
          timestamp: msg.timestamp,
          avatar: undefined, // 🔥 undefined en lugar de null
        }));

        // Establecer los mensajes históricos
        loadHistoricalMessages(formattedMessages);
        loadedAssemblyRef.current = assemblyId; // 🔥 Mark as loaded
        
        console.log(`✅ Cargados ${formattedMessages.length} mensajes históricos`);
      } catch (error) {
        console.error('❌ Error al cargar historial de chat:', error);
        // No mostrar error al usuario, simplemente iniciar con chat vacío
        loadHistoricalMessages([]); // Limpiar mensajes si hay error
      } finally {
        await ensureMinimumLoadingTime(startTime);
        setIsLoadingHistory(false);
      }
    };

    loadChatHistory();
  }, [assemblyId]); // 🔥 SOLO assemblyId en dependencias, no loadHistoricalMessages

  // Auto-scroll inteligente: solo si el usuario está cerca del final
  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShowScrollToBottom(false);
    }
  }, [chatMessages, assemblyId, shouldAutoScroll]);

  useEffect(() => {
    // Al cambiar de asamblea, volver a comportamiento por defecto (seguir últimos mensajes)
    setShouldAutoScroll(true);
  }, [assemblyId]);

  // Filtrar mensajes de la asamblea actual
  const assemblyMessages = chatMessages.filter(msg => msg.assemblyId === assemblyId);

  const handleSendMessage = async (messageOverride?: string) => {
    const messageToSend = (messageOverride ?? newMessage).trim();
    if (!messageToSend || !isConnected || isSending) return;

    try {
      setIsSending(true);
      setSendError(null);
      await sendMessage(assemblyId, messageToSend);
      setNewMessage('');
      setFailedMessage(null);
      handleStopTyping();
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      setFailedMessage(messageToSend);
      setSendError('No se pudo enviar el mensaje. Puedes reintentarlo.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (isTyping) {
        setTyping(assemblyId, false);
      }
    };
  }, [assemblyId, isTyping, setTyping]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);

    // Manejar indicador de "escribiendo"
    if (value.trim() && !isTyping) {
      setIsTyping(true);
      setTyping(assemblyId, true);
    }

    // Reset del timeout de typing
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      handleStopTyping();
    }, 3000);
  };

  const handleStopTyping = () => {
    if (isTyping) {
      setIsTyping(false);
      setTyping(assemblyId, false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const nearBottom = distanceFromBottom < 80;
    setShouldAutoScroll(nearBottom);
    setShowScrollToBottom(distanceFromBottom > 220);
  };

  const handleScrollToBottom = () => {
    setShouldAutoScroll(true);
    setShowScrollToBottom(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDownloadChatPdf = async () => {
    try {
      setIsDownloadingChat(true);
      const blob = await assemblyService.downloadAssemblyChatPdf(assemblyId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat-asamblea-${assemblyId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      notifySuccess('Chat descargado correctamente');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Error al descargar chat');
    } finally {
      setIsDownloadingChat(false);
    }
  };

  const formatTime = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const MessageItem: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isSystemMessage = message.type === 'system';
    
    if (isSystemMessage) {
      return (
        <div className="flex justify-center my-2">
          <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
            {message.message}
          </div>
        </div>
      );
    }

    return (
      <div className="flex space-x-3 p-2 hover:bg-gray-50 rounded">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
            {message.userName.charAt(0).toUpperCase()}
          </div>
        </div>
        
        {/* Contenido del mensaje */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 gap-0.5">
            <span className="text-sm font-medium text-gray-900">
              {message.userName}
            </span>
            <div className="flex items-center space-x-2 min-w-0">
              <span className="text-xs text-gray-500 truncate max-w-[140px] sm:max-w-none">
                {message.userRole}
              </span>
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {formatTime(message.timestamp)}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-700 mt-1 break-words">
            {message.message}
          </p>
        </div>
      </div>
    );
  };

  const TypingIndicator: React.FC = () => {
    const assemblyTypingUsers = typingUsers.filter((user) => !user.assemblyId || user.assemblyId === assemblyId);
    if (assemblyTypingUsers.length === 0) return null;

    const typingText = assemblyTypingUsers.length === 1
      ? `${assemblyTypingUsers[0].userName} está escribiendo...`
      : `${assemblyTypingUsers.length} personas están escribiendo...`;

    return (
      <div className="flex items-center space-x-2 p-2 text-sm text-gray-500">
        <div className="flex space-x-1">
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
        <span className="text-xs">{typingText}</span>
      </div>
    );
  };

  return (
    <div className={`flex flex-col bg-white border rounded-lg ${className} max-h-[520px]`}>
      {/* Header del chat */}
      <div className="flex items-center justify-between p-3 border-b bg-gray-50 rounded-t-lg">
        <h3 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span>Chat de Asamblea</span>
        </h3>
        <div className="flex items-center space-x-2">
          {canDownloadChatPdf && (
            <button
              type="button"
              onClick={handleDownloadChatPdf}
              disabled={isDownloadingChat}
              className="text-xs px-2 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50"
            >
              {isDownloadingChat ? 'Descargando...' : 'Descargar chat PDF'}
            </button>
          )}
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-xs text-gray-500">
            {assemblyMessages.length} mensajes
          </span>
        </div>
      </div>

  {/* Área de mensajes con scroll interno y padding estable */}
  <div className="relative flex-1 min-h-0">
    <div
      ref={messagesContainerRef}
      onScroll={handleMessagesScroll}
      className={`h-full overflow-y-auto ${maxHeight} p-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent`}
    >
        {isLoadingHistory ? (
          <LoadingState className="h-full" />
        ) : assemblyMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <p className="text-sm">No hay mensajes aún</p>
              <p className="text-xs">¡Sé el primero en escribir!</p>
            </div>
          </div>
        ) : (
          <>
            {assemblyMessages.map((message) => (
              <MessageItem key={message.id} message={message} />
            ))}
            <TypingIndicator />
          </>
        )}
        <div ref={messagesEndRef} />
    </div>

    {showScrollToBottom && (
      <button
        type="button"
        onClick={handleScrollToBottom}
        aria-label="Ir al final de los mensajes"
        title="Ir al final"
        className="absolute bottom-3 left-1/2 -translate-x-1/2 h-9 w-9 rounded-full border border-gray-200/80 bg-white/70 text-gray-500 shadow-sm backdrop-blur hover:bg-white hover:text-gray-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-center"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M10 14a1 1 0 01-.707-.293l-4-4a1 1 0 111.414-1.414L10 11.586l3.293-3.293a1 1 0 111.414 1.414l-4 4A1 1 0 0110 14z" clipRule="evenodd" />
        </svg>
      </button>
    )}
  </div>

      {/* Input para nuevo mensaje */}
      <div className="p-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] border-t bg-gray-50 rounded-b-lg">
        {!isConnected && (
          <div className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Sin conexión en tiempo real. Reintentando...
          </div>
        )}
        <div className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onBlur={handleStopTyping}
            placeholder={isConnected ? "Escribe un mensaje..." : "Conectando..."}
            disabled={!isConnected}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base sm:text-sm"
            enterKeyHint="send"
            maxLength={500}
          />
          <button
            onClick={() => void handleSendMessage()}
            disabled={!newMessage.trim() || !isConnected || isSending}
            aria-label="Enviar mensaje"
            className="h-11 w-11 sm:h-auto sm:w-auto sm:px-4 py-2 flex items-center justify-center bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        {sendError && failedMessage && (
          <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-center justify-between gap-2">
            <span className="truncate">{sendError}</span>
            <button
              type="button"
              onClick={() => void handleSendMessage(failedMessage)}
              disabled={!isConnected || isSending}
              className="shrink-0 font-medium underline hover:no-underline disabled:opacity-60"
            >
              Reintentar
            </button>
          </div>
        )}
        {newMessage.length > 450 && (
          <div className="mt-1 text-xs text-gray-500">
            {500 - newMessage.length} caracteres restantes
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatComponent;